import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { styled } from 'baseui';
import { useAtomValue } from 'jotai';
import useActionDuration from '../../../../../common/hooks/useActionDuration';
import { getAnnotationStyle } from '../../../../../common/utils/utils';
import { isVideoLabelingM4EnabledAtom } from '../../../../labelling-v2/atoms/m4-atoms';
import { useStageAtoms } from '../../../../labelling-v2/components/stage-scope/hooks';
import { useDynamicAttributesText } from '../../../../labelling-v2/hooks/use-dynamic-attributes-text';
import { ANNOTATION_TYPE } from '../../../constants';
import { CSS_VARIABLE_STAGE_VISIBLE_SCALE } from '../../../constants/stage-css-variables';
import useStage from '../../../providers/hooks/use-stage';
import { type StagePointerEventT } from '../../../providers/stage-provider';
import { type PointAnnotationT } from '../../../types';
import { isLabelingMode } from '../../toolbar/utils';
import Shape from '../shape/shape';
import { VERTEX_TYPE } from '../shape/vertex';
import { KEY_POINT_RADIUS } from './constants';
import { getTranslatedKeyPoint } from './utils';
const Circle = styled<'circle', {
  $originX: number;
  $originY: number;
}>('circle', ({
  $originX,
  $originY
}) => ({
  transformOrigin: `${$originX}px ${$originY}px`,
  transform: `scale(var(${CSS_VARIABLE_STAGE_VISIBLE_SCALE}))`
}));
type PropsT = {
  isDrawing?: boolean;
  annotation?: PointAnnotationT;
  annotationIdx?: string;
  disableChanges?: boolean;
};
const KeyPoint = ({
  isDrawing,
  annotation,
  annotationIdx,
  disableChanges
}: PropsT) => {
  const {
    stage,
    addAnnotation,
    updateAnnotation,
    getStageDimension
  } = useStage();
  const {
    editorModeAtom,
    editableAnnotationAtom,
    focusedAnnotationIdAtom,
    selectedAnnotationIdsAtom,
    selectedAnnotationLabelAtom
  } = useStageAtoms();
  const editorMode = useAtomValue(editorModeAtom);
  const editableAnnotation = useAtomValue(editableAnnotationAtom);
  const focusedAnnotationId = useAtomValue(focusedAnnotationIdAtom);
  const selectedAnnotationIds = useAtomValue(selectedAnnotationIdsAtom);
  const annotationLabel = useAtomValue(selectedAnnotationLabelAtom);
  const isVideoLabelingM4Enabled = useAtomValue(isVideoLabelingM4EnabledAtom);
  const [drawingPoint, setDrawingPoint] = useState<PointAnnotationT | undefined | null>(annotation);
  const [isVertexSelected, setSelectedVertex] = useState<boolean>(false);
  const isSelected = typeof annotationIdx === 'string' && selectedAnnotationIds.indexOf(annotationIdx) >= 0;
  const {
    start,
    end
  } = useActionDuration();
  const isLocked = Boolean(annotation?.locked);
  useEffect(() => {
    setDrawingPoint(annotation);
  }, [annotation]);
  const isEditable = editableAnnotation === annotationIdx;
  const isFocused = focusedAnnotationId === annotationIdx;
  const getPointDetails = useCallback(position => {
    if (!annotationLabel) return;
    const point: PointAnnotationT = {
      annotationType: ANNOTATION_TYPE.KEY_POINT,
      data: {
        x: position[0],
        y: position[1]
      },
      annotationLabel
    };
    return point;
  }, [annotationLabel]);
  const onPointerDown = useCallback((stageEvent: StagePointerEventT) => {
    const {
      mousePos
    } = stageEvent.detail;
    const point = getPointDetails(mousePos);
    // Drawing key points is instant
    point && addAnnotation(point, {
      actionDuration: '0'
    });
  }, [addAnnotation, getPointDetails]);
  useEffect(() => {
    if (!stage || !isLabelingMode(editorMode)) return;
    if (isDrawing) {
      stage.addEventListener('pointerdown', onPointerDown);
      return () => {
        stage.removeEventListener('pointerdown', onPointerDown);
      };
    }
  }, [isDrawing, stage, onPointerDown, editorMode]);
  const dragPoint = useCallback((annotation, deltaX, deltaY) => {
    const [stageWidth, stageHeight] = getStageDimension();
    return getTranslatedKeyPoint(deltaX, deltaY, annotation, stageWidth, stageHeight);
  }, [getStageDimension]);
  const onDrag = useCallback((stageEvent: StagePointerEventT) => {
    if (!annotation) return;
    if (!isVertexSelected) return;
    const {
      deltaX,
      deltaY
    } = stageEvent.detail;
    const point = dragPoint(annotation, deltaX, deltaY);
    setDrawingPoint(point);
    start();
  }, [annotation, dragPoint, isVertexSelected, start]);
  const onDragEnd = useCallback((stageEvent: StagePointerEventT) => {
    if (!annotation || !annotationIdx) return;
    if (!isVertexSelected) return;
    const {
      deltaX,
      deltaY
    } = stageEvent.detail;
    const point = dragPoint(annotation, deltaX, deltaY);
    if (point) {
      updateAnnotation(annotationIdx, point, {
        actionDuration: end()
      });
      setDrawingPoint(point);
    }
  }, [annotation, annotationIdx, isVertexSelected, dragPoint, updateAnnotation, end]);
  useEffect(() => {
    if (!stage || disableChanges || isLocked || !isLabelingMode(editorMode)) return;
    if (isEditable) {
      stage.addEventListener('drag', onDrag);
      stage.addEventListener('dragend', onDragEnd);
      return () => {
        stage.removeEventListener('drag', onDrag);
        stage.removeEventListener('dragend', onDragEnd);
      };
    }
  }, [isEditable, stage, onDrag, onDragEnd, disableChanges, editorMode, isLocked]);
  const point = isEditable && drawingPoint || isSelected && !isEditable ? drawingPoint : annotation;
  const labelCoords = useMemo(() => {
    if (!point) return null;
    return [point.data.x, point.data.y];
  }, [point]);
  const dynamicAttributesText = useDynamicAttributesText(annotationIdx);
  if (!point) return null;
  const keyPointStyle = getAnnotationStyle({
    fill: point.annotationLabel.style.fillColor,
    fillOpacity: point.annotationLabel.style.fillOpacity,
    strokeColor: point.annotationLabel.style.strokeColor
  }, {
    isFocused,
    isLocked,
    useStageStrokeWidth: true,
    isM4: isVideoLabelingM4Enabled
  });
  if (isVideoLabelingM4Enabled) {
    keyPointStyle.fill = point.annotationLabel.style.strokeColor;
    keyPointStyle.fillOpacity = 1;
    keyPointStyle.strokeWidth = 1;
  }
  return <Shape vertices={[{
    point: [point.data.x, point.data.y],
    type: VERTEX_TYPE.MOVE
  }]} isLocked={isLocked} isDrawing={isDrawing} metadataPillText={dynamicAttributesText} annotationLabel={{
    ...point.annotationLabel,
    // @ts-expect-error todo(ts-migration) TS2322 Type 'number[]' is not assignable to type '[number, number]'.
    labelCoords,
    hideLabelPointer: true
  }} onVertexSelect={() => setSelectedVertex(true)} onVertexDeselect={() => setSelectedVertex(false)} annotationIdx={annotationIdx} dataAttributes={{
    'data-e2e-id': `annotation-${point.annotationLabel.title}-${point.annotationType}`,
    'data-annotation-id': annotationIdx?.toString() || ''
  }}>
      <Circle $originX={point.data.x} $originY={point.data.y} cx={point.data.x} cy={point.data.y} r={KEY_POINT_RADIUS + (isFocused ? 2 : 0)} style={keyPointStyle} />
    </Shape>;
};
export default KeyPoint;